// frontend/src/screens/manager/SwapApprovalScreen.jsx
import React, { useState, useMemo } from 'react';
import { useLayout } from '../../context/LayoutContext';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Divider,
  Breadcrumbs,
  Link,
  Snackbar,
  Stack,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tooltip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { NavigateNext, SwapHoriz, CheckCircle, Cancel, SyncAlt, WarningAmber } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  useGetSwapRequestsForApprovalQuery,
  useApproveSwapRequestMutation,
  useRejectSwapRequestMutation,
} from '../../slices/swapApiSlice';

const formatDateTime = (d) =>
  new Date(d).toLocaleString('en-IE', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

// ECT staff (e.g. "ECT-HCA") are required to swap on a reciprocal basis —
// the person who receives their shift must give one back in return. This
// checks the category on the shift snapshot rather than either staff
// member's own jobtitle, since the snapshot already carries the exact
// CATEGORY_OF_STAFF value the shift was created against.
const isEctCategory = (categoryOfStaff) =>
  String(categoryOfStaff || '').toUpperCase().startsWith('ECT');

/**
 * Builds a lookup of swapRequest._id → { requiresReciprocal, isReciprocated, pairId }
 * by scanning the currently-loaded pending-approval list for a matching
 * reverse swap (same two staff members, direction flipped).
 */
const buildReciprocalMap = (swapRequests) => {
  const map = new Map();
  if (!swapRequests) return map;

  for (const r of swapRequests) {
    const requiresReciprocal = isEctCategory(r.shiftSnapshot?.categoryOfStaff);
    if (!requiresReciprocal) {
      map.set(r._id, { requiresReciprocal: false, isReciprocated: false, pairId: null });
      continue;
    }

    const pair = swapRequests.find(
      (other) =>
        other._id !== r._id &&
        String(other.requestingStaffId) === String(r.acceptedByStaffId) &&
        String(other.acceptedByStaffId) === String(r.requestingStaffId)
    );

    map.set(r._id, {
      requiresReciprocal: true,
      isReciprocated: !!pair,
      pairId: pair ? pair._id : null,
    });
  }

  return map;
};

const SwapApprovalScreen = () => {
  const { setFluid } = useLayout();
  const navigate = useNavigate();
  const { userInfo } = useSelector((state) => state.auth);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  React.useEffect(() => {
    setFluid(true);
    return () => setFluid(false);
  }, [setFluid]);

  const [decisionDialog, setDecisionDialog] = useState({ open: false, swapRequest: null, action: null });
  const [comment, setComment] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const {
    data: swapRequests,
    isLoading,
    error,
    refetch,
  } = useGetSwapRequestsForApprovalQuery(undefined, {
    pollingInterval: 60000, // approvals can come from multiple managers — keep the queue fresh
  });

  const reciprocalMap = useMemo(() => buildReciprocalMap(swapRequests), [swapRequests]);

  const [approveSwapRequest, { isLoading: approving }] = useApproveSwapRequestMutation();
  const [rejectSwapRequest, { isLoading: rejecting }] = useRejectSwapRequestMutation();

  const showSnackbar = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

  // Client-side guard so non-managers get a clear message rather than an
  // empty/broken screen — the backend enforces the real check regardless.
  const isAuthorized = userInfo?.isManager || userInfo?.isAdmin;

  const openDecisionDialog = (swapRequest, action) => {
    setComment('');
    setDecisionDialog({ open: true, swapRequest, action });
  };

  const closeDecisionDialog = () => setDecisionDialog({ open: false, swapRequest: null, action: null });

  const handleConfirmDecision = async () => {
    const { swapRequest, action } = decisionDialog;
    closeDecisionDialog();

    try {
      if (action === 'approve') {
        await approveSwapRequest({ id: swapRequest._id, comment }).unwrap();
        showSnackbar('Swap approved — shift has been reassigned', 'success');
      } else {
        await rejectSwapRequest({ id: swapRequest._id, comment }).unwrap();
        showSnackbar('Swap rejected — original staff member keeps the shift', 'success');
      }
    } catch (err) {
      showSnackbar(err?.data?.message || err?.error || 'Could not record this decision', 'error');
      refetch(); // pull current state in case eligibility changed server-side
    }
  };

  if (!isAuthorized) {
    return (
      <Box sx={{ p: isMobile ? 1.5 : 3 }}>
        <Alert severity="error">You don't have permission to view swap approvals.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: isMobile ? 1.5 : 3 }}>
      <Breadcrumbs separator={<NavigateNext fontSize="small" />} sx={{ mb: 1.5 }}>
        <Link underline="hover" color="inherit" href="#" onClick={(e) => { e.preventDefault(); navigate('/manager/dashboard'); }}>
          Manager Dashboard
        </Link>
        <Typography color="text.primary" variant={isMobile ? 'body2' : 'body1'}>
          Shift Swap Approvals
        </Typography>
      </Breadcrumbs>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <SwapHoriz color="primary" />
        <Typography variant={isMobile ? 'h5' : 'h4'}>Shift Swap Approvals</Typography>
      </Box>

      {/* Legend for the ECT reciprocal-swap coloring */}
      <Stack direction="row" spacing={2} sx={{ mb: 1.5, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Box sx={{ width: 14, height: 14, bgcolor: '#fff3e0', border: '1px solid #ffb74d', borderRadius: 0.5 }} />
          <Typography variant="caption" color="text.secondary">ECT swap — no reciprocal shift found yet</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Box sx={{ width: 14, height: 14, bgcolor: '#e8f5e9', border: '1px solid #66bb6a', borderRadius: 0.5 }} />
          <Typography variant="caption" color="text.secondary">ECT swap — reciprocal pair matched</Typography>
        </Box>
      </Stack>

      <Paper elevation={2} sx={{ p: isMobile ? 1.5 : 2 }}>
        {isLoading && (
          <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
        )}

        {error && <Alert severity="error">Could not load pending swap approvals.</Alert>}

        {!isLoading && swapRequests?.length === 0 && (
          <Alert severity="info">No swaps are currently awaiting approval.</Alert>
        )}

        <List>
          {swapRequests?.map((r, idx) => {
            const reciprocalInfo = reciprocalMap.get(r._id) || {};
            const { requiresReciprocal, isReciprocated } = reciprocalInfo;

            const rowBgColor = !requiresReciprocal
              ? 'transparent'
              : isReciprocated
                ? '#e8f5e9' // green — matched pair
                : '#fff3e0'; // orange — single, unmatched

            return (
              <React.Fragment key={r._id}>
                {idx > 0 && <Divider component="li" />}
                <ListItem
                  sx={{ py: 2, bgcolor: rowBgColor, borderRadius: 1 }}
                  secondaryAction={
                    <Stack direction={isMobile ? 'column' : 'row'} spacing={1}>
                      <Button
                        size="small"
                        color="error"
                        variant="outlined"
                        startIcon={<Cancel />}
                        onClick={() => openDecisionDialog(r, 'reject')}
                        disabled={approving || rejecting}
                      >
                        Reject
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        color="success"
                        startIcon={<CheckCircle />}
                        onClick={() => openDecisionDialog(r, 'approve')}
                        disabled={approving || rejecting}
                      >
                        Approve
                      </Button>
                    </Stack>
                  }
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                          <Typography variant="body2" fontWeight={600}>
                            {formatDateTime(r.shiftSnapshot?.startDateTime)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            to {formatDateTime(r.shiftSnapshot?.endDateTime)}
                          </Typography>
                        </Box>

                        {requiresReciprocal && (
                          isReciprocated ? (
                            <Tooltip title="A matching reciprocal shift from this same pair of staff is also pending approval">
                              <Chip
                                icon={<SyncAlt sx={{ fontSize: 14 }} />}
                                label="Reciprocal matched"
                                size="small"
                                sx={{ bgcolor: '#66bb6a', color: 'white', height: 22, fontSize: '0.7rem' }}
                              />
                            </Tooltip>
                          ) : (
                            <Tooltip title="ECT staff must swap on a reciprocal basis — no matching shift back to the requester has been found yet">
                              <Chip
                                icon={<WarningAmber sx={{ fontSize: 14 }} />}
                                label="No reciprocal shift yet"
                                size="small"
                                sx={{ bgcolor: '#ffb74d', color: 'white', height: 22, fontSize: '0.7rem' }}
                              />
                            </Tooltip>
                          )
                        )}
                      </Box>
                    }
                    secondary={
                      <>
                        <Box component="span" sx={{ display: 'block' }}>
                          {r.shiftSnapshot?.wardDepartmentArea} · {r.shiftSnapshot?.categoryOfStaff}
                        </Box>
                        <Box component="span" sx={{ display: 'block' }}>
                          <Chip label="From" size="small" sx={{ height: 18, fontSize: '0.65rem', mr: 0.5 }} />
                          {r.requestingStaffName}
                        </Box>
                        <Box component="span" sx={{ display: 'block' }}>
                          <Chip label="To" size="small" color="success" sx={{ height: 18, fontSize: '0.65rem', mr: 0.5 }} />
                          {r.acceptedByName} ({r.acceptedByEmail})
                        </Box>
                        {r.reason && (
                          <Box component="span" sx={{ display: 'block' }}>
                            Original reason: "{r.reason}"
                          </Box>
                        )}
                      </>
                    }
                    sx={{ pr: isMobile ? 0 : 24, mb: isMobile ? 1 : 0 }}
                  />
                </ListItem>
              </React.Fragment>
            );
          })}
        </List>
      </Paper>

      {/* Confirm approve/reject dialog */}
      <Dialog open={decisionDialog.open} onClose={closeDecisionDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {decisionDialog.action === 'approve' ? 'Approve this swap?' : 'Reject this swap?'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {decisionDialog.swapRequest && (
              decisionDialog.action === 'approve' ? (
                <>
                  This reassigns the shift on{' '}
                  <strong>{formatDateTime(decisionDialog.swapRequest.shiftSnapshot?.startDateTime)}</strong> from{' '}
                  <strong>{decisionDialog.swapRequest.requestingStaffName}</strong> to{' '}
                  <strong>{decisionDialog.swapRequest.acceptedByName}</strong>. Both staff members will be notified.
                </>
              ) : (
                <>
                  This keeps the shift on{' '}
                  <strong>{formatDateTime(decisionDialog.swapRequest.shiftSnapshot?.startDateTime)}</strong> with{' '}
                  <strong>{decisionDialog.swapRequest.requestingStaffName}</strong>. Both staff members will be notified.
                </>
              )
            )}
            {decisionDialog.action === 'approve'
              && reciprocalMap.get(decisionDialog.swapRequest?._id)?.requiresReciprocal
              && !reciprocalMap.get(decisionDialog.swapRequest?._id)?.isReciprocated && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                This is an ECT swap with no matching reciprocal shift currently pending. Confirm this is intended before approving.
              </Alert>
            )}
          </DialogContentText>
          <TextField
            label="Comment (optional)"
            multiline
            minRows={2}
            fullWidth
            size="small"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDecisionDialog}>Cancel</Button>
          <Button
            variant="contained"
            color={decisionDialog.action === 'approve' ? 'success' : 'error'}
            onClick={handleConfirmDecision}
            disabled={approving || rejecting}
          >
            {decisionDialog.action === 'approve'
              ? (approving ? 'Approving…' : 'Confirm Approve')
              : (rejecting ? 'Rejecting…' : 'Confirm Reject')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        message={snackbar.message}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};

export default SwapApprovalScreen;